import {
  ChangeDetectionStrategy, ChangeDetectorRef, Component,
  inject, input, OnDestroy, OnInit, signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FikoYTKokBilesenComponent } from '@service/kok-bilesen/kok-bilesen.component';
import { KartWebSocketService } from './services/kart-websocket.service';
import { Subject, takeUntil } from 'rxjs';

/** Paylaşım sisteminde gösterilecek NodeMCU kart okuyucu durum kartı */
@Component({
  selector: 'kart-okuyucu-paylasim-kart',
  standalone: true,
  imports: [CommonModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="ko-pk-kart" [class.bagli]="bagli()" [class.kart-var]="kartVar()">

      <!-- Başlık -->
      <div class="ko-pk-baslik-row">
        <div class="ko-pk-ikon-wrap" [class.pulse]="kartVar()">
          <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
            <rect x="2" y="5" width="20" height="14" rx="2"/>
            <line x1="2" y1="10" x2="22" y2="10"/>
          </svg>
        </div>
        <span class="ko-pk-baslik">Kart Okuyucu</span>
        <div class="ko-pk-durum-chip" [class.on]="bagli()" [class.off]="!bagli()">
          <span class="ko-pk-led"></span>
          {{ bagli() ? (baglantiModu() === 'websocket' ? 'WiFi' : 'Seri') : 'Çevrimdışı' }}
        </div>
      </div>

      <!-- İçerik -->
      <div class="ko-pk-govde">

        @if (!bagli()) {
          <!-- Bağlı değil -->
          <div class="ko-pk-offline-satir">
            <svg viewBox="0 0 24 24" width="28" height="28" fill="none" stroke="currentColor" stroke-width="1.5" opacity="0.35"><line x1="1" y1="1" x2="23" y2="23"/><path d="M16.72 11.06A10.94 10.94 0 0 1 19 12.55M5 12.55a10.94 10.94 0 0 1 5.17-2.39M10.71 5.05A16 16 0 0 1 22.56 9M1.42 9a15.91 15.91 0 0 1 4.7-2.88M8.53 16.11a6 6 0 0 1 6.95 0M12 20h.01"/></svg>
            <span class="ko-pk-offline-yazi">Cihaz bağlantısı yok</span>
          </div>
        } @else if (kartVar()) {
          <!-- Kart var — kişi bilgisi -->
          <div class="ko-pk-kisi-wrap">
            <div class="ko-pk-avatar" [class.animli]="true">
              @if (kisiResim()) {
                <img [src]="kisiResim()" class="ko-pk-avatar-img" alt="" />
              } @else {
                <span class="ko-pk-avatar-harf">{{ kisiIsim()?.[0] || '?' }}</span>
              }
              <span class="ko-pk-avatar-rozet">✓</span>
            </div>
            <div class="ko-pk-kisi-bilgi">
              <div class="ko-pk-kisi-isim">{{ kisiIsim() || 'Tanınmadı' }}</div>
              <div class="ko-pk-kisi-uid">UID: {{ sonUid() }}</div>
              <div class="ko-pk-sure-row">
                <svg viewBox="0 0 16 16" width="10" height="10"><circle cx="8" cy="8" r="7" fill="none" stroke="currentColor" stroke-width="1.5"/><polyline points="8,4 8,8 11,10" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>
                <span class="ko-pk-sure">{{ kartSure() }}</span>
              </div>
            </div>
          </div>
        } @else {
          <!-- Bekleme -->
          <div class="ko-pk-bekleme-wrap">
            <div class="ko-pk-nfc-animasyon">
              <div class="ko-pk-nfc-halka h1"></div>
              <div class="ko-pk-nfc-halka h2"></div>
              <div class="ko-pk-nfc-halka h3"></div>
              <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="1.6" class="ko-pk-nfc-svg">
                <path d="M6.62 10.79a15.053 15.053 0 006.59 6.59l2.2-2.2a1 1 0 011.01-.24c1.12.37 2.33.57 3.57.57a1 1 0 011 1V20a1 1 0 01-1 1A17 17 0 013 4a1 1 0 011-1h3.5a1 1 0 011 1c0 1.25.2 2.45.57 3.57a1 1 0 01-.25 1.02L6.62 10.79z"/>
              </svg>
            </div>
            <div class="ko-pk-bekleme-yazi">Kart bekleniyor...</div>
          </div>
        }

      </div>

      <!-- Alt bar -->
      <div class="ko-pk-alt-bar">
        <span class="ko-pk-cihaz">
          <svg viewBox="0 0 24 24" width="10" height="10" fill="none" stroke="currentColor" stroke-width="2"><rect x="5" y="2" width="14" height="20" rx="2"/><circle cx="12" cy="17" r="1"/></svg>
          {{ parametreler()?.cihazIsmi || parametreler()?.cihazKimlik || 'NodeMCU' }}
        </span>
        <span class="ko-pk-zaman">{{ sonOkumaZaman() }}</span>
      </div>

    </div>
  `,
  styles: [`
    .ko-pk-kart {
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
      border-radius: 12px;
      background: linear-gradient(135deg, #1a1a2e 0%, #16213e 60%, #0f3460 100%);
      border: 1px solid rgba(0,255,200,0.15);
      overflow: hidden;
      min-width: 220px;
      max-width: 320px;
      color: #e0e0e0;
      transition: border-color 0.3s, box-shadow 0.3s;
    }
    .ko-pk-kart.bagli {
      border-color: rgba(0,255,180,0.25);
      box-shadow: 0 0 16px rgba(0,255,180,0.08);
    }
    .ko-pk-kart.kart-var {
      border-color: rgba(0,220,255,0.4);
      box-shadow: 0 0 24px rgba(0,220,255,0.12);
    }

    /* Başlık */
    .ko-pk-baslik-row {
      display: flex;
      align-items: center;
      gap: 7px;
      padding: 10px 12px 8px;
      border-bottom: 1px solid rgba(255,255,255,0.06);
    }
    .ko-pk-ikon-wrap {
      color: rgba(0,220,255,0.7);
      line-height: 0;
      transition: filter 0.3s;
    }
    .ko-pk-ikon-wrap.pulse {
      filter: drop-shadow(0 0 4px rgba(0,220,255,0.8));
      animation: pk-pulse-ikon 2s ease-in-out infinite;
    }
    @keyframes pk-pulse-ikon {
      0%,100% { filter: drop-shadow(0 0 3px rgba(0,220,255,0.5)); }
      50%      { filter: drop-shadow(0 0 8px rgba(0,220,255,1)); }
    }
    .ko-pk-baslik {
      flex: 1;
      font-size: 12px;
      font-weight: 600;
      letter-spacing: 0.5px;
      color: #ccc;
    }
    .ko-pk-durum-chip {
      display: flex;
      align-items: center;
      gap: 4px;
      font-size: 10px;
      font-weight: 600;
      padding: 3px 8px;
      border-radius: 50px;
      background: rgba(255,255,255,0.06);
    }
    .ko-pk-durum-chip.on  { color: #00e6b0; background: rgba(0,230,176,0.12); }
    .ko-pk-durum-chip.off { color: #888; }
    .ko-pk-led {
      width: 6px; height: 6px;
      border-radius: 50%;
      background: currentColor;
    }
    .ko-pk-durum-chip.on .ko-pk-led {
      box-shadow: 0 0 4px currentColor;
      animation: pk-led-blink 2s ease-in-out infinite;
    }
    @keyframes pk-led-blink {
      0%,100% { opacity: 1; } 50% { opacity: 0.4; }
    }

    /* Gövde */
    .ko-pk-govde {
      padding: 12px;
      min-height: 90px;
      display: flex;
      align-items: center;
    }

    /* Offline */
    .ko-pk-offline-satir {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 6px;
      width: 100%;
      text-align: center;
    }
    .ko-pk-offline-yazi {
      font-size: 11px;
      color: #666;
    }

    /* Kişi */
    .ko-pk-kisi-wrap {
      display: flex;
      align-items: center;
      gap: 12px;
      width: 100%;
    }
    .ko-pk-avatar {
      position: relative;
      flex-shrink: 0;
    }
    .ko-pk-avatar-img {
      width: 46px; height: 46px;
      border-radius: 50%;
      object-fit: cover;
      border: 2px solid rgba(0,220,255,0.4);
    }
    .ko-pk-avatar-harf {
      width: 46px; height: 46px;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 18px;
      font-weight: 700;
      background: linear-gradient(135deg, #00c6ff, #0072ff);
      color: white;
      border: 2px solid rgba(0,220,255,0.5);
      box-shadow: 0 0 12px rgba(0,220,255,0.3);
    }
    .ko-pk-avatar.animli .ko-pk-avatar-harf {
      animation: pk-avatar-glow 3s ease-in-out infinite;
    }
    @keyframes pk-avatar-glow {
      0%,100% { box-shadow: 0 0 8px rgba(0,220,255,0.3); }
      50%      { box-shadow: 0 0 20px rgba(0,220,255,0.7); }
    }
    .ko-pk-avatar-rozet {
      position: absolute;
      bottom: -2px; right: -2px;
      width: 16px; height: 16px;
      border-radius: 50%;
      background: #00e676;
      color: #000;
      font-size: 9px;
      display: flex;
      align-items: center;
      justify-content: center;
      font-weight: 900;
      border: 1.5px solid #1a1a2e;
    }
    .ko-pk-kisi-bilgi { flex: 1; min-width: 0; }
    .ko-pk-kisi-isim {
      font-size: 13px;
      font-weight: 700;
      color: #fff;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
    .ko-pk-kisi-uid {
      font-size: 9px;
      color: rgba(0,220,255,0.6);
      font-family: 'Courier New', monospace;
      margin-top: 2px;
    }
    .ko-pk-sure-row {
      display: flex;
      align-items: center;
      gap: 4px;
      margin-top: 4px;
      color: rgba(0,230,176,0.8);
      font-size: 11px;
      font-weight: 600;
    }

    /* Bekleme */
    .ko-pk-bekleme-wrap {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 8px;
      width: 100%;
    }
    .ko-pk-nfc-animasyon {
      position: relative;
      width: 54px; height: 54px;
      display: flex;
      align-items: center;
      justify-content: center;
    }
    .ko-pk-nfc-halka {
      position: absolute;
      border-radius: 50%;
      border: 1.5px solid rgba(0,220,255,0.4);
      animation: pk-nfc-expand 2.4s ease-out infinite;
    }
    .ko-pk-nfc-halka.h1 { width: 20px; height: 20px; animation-delay: 0s; }
    .ko-pk-nfc-halka.h2 { width: 36px; height: 36px; animation-delay: 0.6s; }
    .ko-pk-nfc-halka.h3 { width: 52px; height: 52px; animation-delay: 1.2s; }
    @keyframes pk-nfc-expand {
      0%   { transform: scale(0.6); opacity: 0.8; }
      100% { transform: scale(1.4); opacity: 0; }
    }
    .ko-pk-nfc-svg { position: relative; z-index: 1; color: rgba(0,220,255,0.7); }
    .ko-pk-bekleme-yazi {
      font-size: 11px;
      color: rgba(0,220,255,0.5);
      animation: pk-bekleme-flas 1.8s ease-in-out infinite;
    }
    @keyframes pk-bekleme-flas {
      0%,100% { opacity: 0.5; } 50% { opacity: 1; }
    }

    /* Alt bar */
    .ko-pk-alt-bar {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 5px 12px 7px;
      border-top: 1px solid rgba(255,255,255,0.05);
      font-size: 9px;
      color: rgba(255,255,255,0.3);
    }
    .ko-pk-cihaz {
      display: flex;
      align-items: center;
      gap: 3px;
    }
  `],
})
export class KartOkuyucuKartComponent extends FikoYTKokBilesenComponent implements OnInit, OnDestroy {
  parametreler = input<any>();

  bagli        = signal(false);
  kartVar      = signal(false);
  baglantiModu = signal<'seri' | 'websocket'>('websocket');
  kisiIsim     = signal<string>('');
  kisiResim    = signal<string>('');
  sonUid       = signal<string>('');
  kartSure     = signal<string>('00:00');
  sonOkumaZaman = signal<string>('--:--');

  private _destroy$ = new Subject<void>();
  private _sureInterval: any = null;
  private _kartBaslangic: number | null = null;

  private wsService = inject(KartWebSocketService);
  private cd = inject(ChangeDetectorRef);

  constructor() { super(); }

  ngOnInit() {
    // Parametrelerden cihaz kimliği ve ismi oku
    const params = this.parametreler?.() ?? {};
    if (params.cihazKimlik) {
      this.wsService.cihazFiltresiAyarla(params.cihazKimlik);
    }

    // WS bağlantı durumu
    this.wsService.durum$.pipe(takeUntil(this._destroy$)).subscribe(durum => {
      this.bagli.set(durum === 'bagli');
      this.baglantiModu.set('websocket');
      if (durum !== 'bagli') {
        this.kartVar.set(false);
        this.kisiIsim.set('');
        this.kisiResim.set('');
        // Bağlantı koptu → okuyucuda kart yok sayılır, kart-sahibi belirteci kapanır
        this.wsService.aktifKartUid$.next(null);
        this._sureDurdur();
      }
      this.cd.markForCheck();
    });

    // Kart olayları
    this.wsService.kartOlayi$.pipe(takeUntil(this._destroy$)).subscribe(olay => {
      if (olay.durum === 'koyuldu' && olay.kartKimlik) {
        this.kartVar.set(true);
        this.sonUid.set(olay.kartKimlik);
        this.kisiIsim.set(params.kisiIsim || '');
        this.kisiResim.set(params.kisiResim || '');
        // Kart-sahibi widget'ının belirteci için aktif UID yayını
        this.wsService.aktifKartUid$.next(olay.kartKimlik);
        this.sonOkumaZamanGuncelle();
        this._sureBaslat(olay.kartUptimeSn);
      } else if (olay.durum === 'cekildi') {
        this.kartVar.set(false);
        this.kisiIsim.set('');
        this.kisiResim.set('');
        this.wsService.aktifKartUid$.next(null);
        this._sureDurdur();
      }
      this.cd.markForCheck();
    });
  }

  ngOnDestroy() {
    super.ngOnDestroy();
    this._destroy$.next();
    this._destroy$.complete();
    this._sureDurdur();
  }

  private _sureBaslat(uptimeSn?: number) {
    this._sureDurdur();
    this._kartBaslangic = uptimeSn != null && uptimeSn >= 0
      ? Date.now() - (uptimeSn * 1000)
      : Date.now();

    this._sureInterval = setInterval(() => {
      if (!this._kartBaslangic) return;
      const diff = Math.floor((Date.now() - this._kartBaslangic) / 1000);
      const m = Math.floor(diff / 60);
      const s = diff % 60;
      this.kartSure.set(`${m.toString().padStart(2,'0')}:${s.toString().padStart(2,'0')}`);
      this.cd.markForCheck();
    }, 1000);
  }

  private _sureDurdur() {
    if (this._sureInterval) { clearInterval(this._sureInterval); this._sureInterval = null; }
    this._kartBaslangic = null;
    this.kartSure.set('00:00');
  }

  private sonOkumaZamanGuncelle() {
    const now = new Date();
    this.sonOkumaZaman.set(
      `${now.getHours().toString().padStart(2,'0')}:${now.getMinutes().toString().padStart(2,'0')}`
    );
  }
}
